// referrals — shared types, queries and mutations for the customer referral
// program (Aug 20, 2026).
//
// The program: a customer refers a neighbor. The referring deal's RM owns the
// follow-up (TL + ASM can act too). When the referred job's BUILD COMPLETES,
// $250 becomes owed to the referrer. Since Oct 20, 2026 (Greg) the fee approves
// itself once that job's first payment is in (referral_try_auto_approve);
// finance can approve one sooner. Shannon pays it.
//
// Single source of truth so the Referrals board (/referrals) and the Finance
// board's Referrals tab can never drift on status labels or payout rules.
//
// Money moves ONLY through the SECURITY DEFINER RPCs below -- they write
// finance_audit_log rows and enforce the earned -> approved -> paid order. A
// BEFORE UPDATE trigger (trg_referrals_payout_guard) rejects any payout-column
// write from a non-finance session, so an RM can work a referral but can never
// pay one.

import { supabase } from "./supabase";

export const REFERRAL_FEE = 250;

export type ReferralStatus = "new" | "contacted" | "appt_scheduled" | "no_thanks" | "unqualified";
export type ReferralPayoutStatus = "pending" | "earned" | "approved" | "paid" | "void";

export interface Referral {
  id: string;
  // Referrer (the existing customer who gets the $250)
  referrer_name: string;
  referrer_phone: string | null;
  referrer_email: string | null;
  referrer_deal_id: string | null;
  referrer_street: string | null;
  referrer_city: string | null;
  referrer_state: string | null;
  referrer_zip: string | null;
  // The person they referred
  referred_name: string;
  referred_phone: string | null;
  referred_street: string | null;
  referred_city: string | null;
  referred_state: string | null;
  referred_zip: string | null;
  referred_deal_id: string | null;
  // Workflow
  status: ReferralStatus;
  assigned_to: string | null;
  contacted_at: string | null;
  resolved_at: string | null;
  resolution_note: string | null;
  last_touch_at: string | null;
  notes: string | null;
  source: string | null;
  created_at: string;
  created_by: string | null;
  // Duplicate flag — we still pay, but Chad should look into it
  duplicate_of_deal_id: string | null;
  duplicate_note: string | null;
  // Payout
  payout_amount: number | null;
  payout_status: ReferralPayoutStatus;
  payout_date: string | null;
  payout_method: string | null;
  payout_reference: string | null;
  payout_note: string | null;
  payout_approved_by: string | null;
  payout_approved_at: string | null;
  payout_paid_by: string | null;
  earned_at: string | null;
  qualifying_event: string | null;
  /** Referred job's claim was called in. Milestone only -- the fee is still
   *  owed at build completion, not here. */
  claim_filed_at: string | null;
  /** Another customer referred this same homeowner FIRST and wins the fee.
   *  Flagged for Chad to void -- never auto-voided (Greg, Aug 20 2026). */
  superseded_by_referral_id: string | null;
}

/** Embeds kept to ONE FK per table — the Jul 14 double-FK embed trap. The
 *  referring/referred deals both point at `deals`, so they're named explicitly. */
export const REFERRAL_SELECT = `
  id, referrer_name, referrer_phone, referrer_email, referrer_deal_id,
  referrer_street, referrer_city, referrer_state, referrer_zip,
  referred_name, referred_phone, referred_street, referred_city, referred_state,
  referred_zip, referred_deal_id, status, assigned_to, contacted_at, resolved_at,
  resolution_note, last_touch_at, notes, source, created_at, created_by,
  duplicate_of_deal_id, duplicate_note,
  payout_amount, payout_status, payout_date, payout_method, payout_reference,
  payout_note, payout_approved_by, payout_approved_at, payout_paid_by,
  earned_at, qualifying_event, claim_filed_at, superseded_by_referral_id
`;

export const STATUS_LABELS: Record<ReferralStatus, string> = {
  new: "New",
  contacted: "Contacted",
  appt_scheduled: "Appointment set",
  no_thanks: "No thanks",
  unqualified: "Not qualified",
};

export const STATUS_CLASSES: Record<ReferralStatus, string> = {
  new: "bg-orange-100 text-orange-700",
  contacted: "bg-amber-100 text-amber-700",
  appt_scheduled: "bg-emerald-100 text-emerald-700",
  no_thanks: "bg-slate-100 text-slate-600",
  unqualified: "bg-slate-100 text-slate-600",
};

export const PAYOUT_LABELS: Record<ReferralPayoutStatus, string> = {
  pending: "Not yet earned",
  earned: "Owed — not yet approved",
  approved: "Approved — ready to pay",
  paid: "Paid",
  void: "Void",
};

export const PAYOUT_CLASSES: Record<ReferralPayoutStatus, string> = {
  pending: "bg-slate-100 text-slate-600",
  earned: "bg-amber-100 text-amber-800 ring-1 ring-amber-300",
  approved: "bg-sky-100 text-sky-800",
  paid: "bg-emerald-100 text-emerald-700",
  void: "bg-slate-100 text-slate-400 line-through",
};

export const OPEN_STATUSES: ReferralStatus[] = ["new", "contacted"];

export function referredAddress(r: Referral): string {
  return [r.referred_street, r.referred_city, r.referred_state].filter(Boolean).join(", ");
}
export function referrerAddress(r: Referral): string {
  return [r.referrer_street, r.referrer_city, r.referrer_state, r.referrer_zip]
    .filter(Boolean).join(", ");
}
export function money(n: number | null | undefined): string {
  return `$${Number(n ?? 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
/** Whole days since an ISO timestamp. */
export function daysSince(iso: string | null | undefined): number | null {
  if (!iso) return null;
  return Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);
}
export function telHref(phone: string | null): string | null {
  if (!phone) return null;
  const d = phone.replace(/\D/g, "");
  return d.length >= 10 ? `tel:+1${d.slice(-10)}` : null;
}
export function smsHref(phone: string | null): string | null {
  const t = telHref(phone);
  return t ? t.replace("tel:", "sms:") : null;
}

// ---------------------------------------------------------------------------
// Reads
// ---------------------------------------------------------------------------

export async function fetchReferrals(): Promise<Referral[]> {
  const { data, error } = await supabase
    .from("referrals")
    .select(REFERRAL_SELECT)
    .order("created_at", { ascending: false });
  if (error) throw error;
  return (data ?? []) as unknown as Referral[];
}

/** Referrals GIVEN BY one customer's deal — powers the deal-page card. */
export async function fetchReferralsForDeal(dealId: string): Promise<Referral[]> {
  const { data, error } = await supabase
    .from("referrals")
    .select(REFERRAL_SELECT)
    .eq("referrer_deal_id", dealId)
    .order("created_at", { ascending: false });
  if (error) throw error;
  return (data ?? []) as unknown as Referral[];
}

// ---------------------------------------------------------------------------
// Workflow mutations (RLS: assigned RM / referring deal's owner+split / managers)
// ---------------------------------------------------------------------------

export async function markContacted(id: string, note?: string) {
  const { error } = await supabase
    .from("referrals")
    .update({
      status: "contacted",
      contacted_at: new Date().toISOString(),
      last_touch_at: new Date().toISOString(),
      ...(note ? { resolution_note: note } : {}),
    })
    .eq("id", id);
  if (error) throw error;
}

/** "No thanks" / not qualified. Voids the fee — nothing will ever build. */
export async function closeReferral(
  id: string,
  status: Extract<ReferralStatus, "no_thanks" | "unqualified">,
  note: string,
) {
  const { error } = await supabase
    .from("referrals")
    .update({
      status,
      resolved_at: new Date().toISOString(),
      resolution_note: note || null,
      last_touch_at: new Date().toISOString(),
    })
    .eq("id", id);
  if (error) throw error;

  // payout_status is finance-only at the table level, so the void goes through
  // the audited RPC. Best-effort: an RM closing a dead referral shouldn't fail
  // on a permission error they can't do anything about — the row is already
  // closed and a `pending` payout can never become earned without a build.
  // NOTE: supabase.rpc RESOLVES with { error } -- it does not reject -- so a
  // rejection handler here would be dead code. Check explicitly.
  const { error: voidErr } = await supabase.rpc("referral_void_payout", {
    p_referral_id: id,
    p_reason: `Referral closed: ${STATUS_LABELS[status]}${note ? ` — ${note}` : ""}`,
  });
  if (voidErr) {
    // Expected for an RM (payout fields are finance-only) and harmless: the
    // row is already closed, and a `pending` payout can never become `earned`
    // without a linked deal completing a build. Finance's "In flight" tile
    // excludes no_thanks/unqualified so this can't inflate the liability.
    console.info("[referrals] payout left pending (finance-only void):", voidErr.message);
  }
}

export async function reopenReferral(id: string) {
  const { error } = await supabase
    .from("referrals")
    .update({ status: "contacted", resolved_at: null, last_touch_at: new Date().toISOString() })
    .eq("id", id);
  if (error) throw error;
}

/** Link the referral to the deal it became (sets status=appt_scheduled and
 *  stamps deals.referral_source_deal_id / referred_by / lead_source). */
export async function linkReferralToDeal(referralId: string, dealId: string) {
  const { error } = await supabase.rpc("referral_link_deal", {
    p_referral_id: referralId,
    p_deal_id: dealId,
  });
  if (error) throw error;
}

/** Staff-logged referral (RM hears it on the phone / at the door). */
export async function logStaffReferral(input: {
  referrerDealId: string; name: string; phone?: string; street?: string;
  city?: string; state?: string; zip?: string; notes?: string;
}) {
  const { data, error } = await supabase.rpc("staff_create_referral", {
    p_referrer_deal: input.referrerDealId,
    p_name: input.name,
    p_phone: input.phone || null,
    p_street: input.street || null,
    p_city: input.city || null,
    p_state: input.state || null,
    p_zip: input.zip || null,
    p_notes: input.notes || null,
  });
  if (error) throw error;
  return data as { ok: boolean; referral_id: string; duplicate: boolean };
}

// ---------------------------------------------------------------------------
// Payout mutations (finance-tier only; each writes a finance_audit_log row)
// ---------------------------------------------------------------------------

/**
 * Approve the fee by hand. Fees also approve themselves once the referred job's
 * first payment is in (Oct 20, 2026); this is for approving one sooner.
 *
 * `outstanding` is the referred job's remaining balance. The gate is SOFT by
 * design (Greg, Aug 20 2026): build_completed_date means BUILT, not COLLECTED,
 * so a fee routinely becomes owed before the money is in -- but a delinquent
 * customer's REFERRER shouldn't be punished for someone else's non-payment.
 * Same shape as the commission board's soft payment gate.
 *
 * Passing it non-zero stamps the audit row `source: 'soft_gate_override'` plus
 * the amount, so "which fees did we approve before collecting?" is answerable.
 */
export async function approvePayout(id: string, note?: string, outstanding?: number) {
  const { error } = await supabase.rpc("referral_approve_payout", {
    p_referral_id: id,
    p_note: note || null,
    p_outstanding: outstanding && outstanding > 0 ? outstanding : null,
  });
  if (error) throw error;
}

export async function markPayoutPaid(
  id: string, method?: string, reference?: string, note?: string,
) {
  const { error } = await supabase.rpc("referral_mark_paid", {
    p_referral_id: id, p_method: method || null,
    p_reference: reference || null, p_note: note || null,
  });
  if (error) throw error;
}

export async function voidPayout(id: string, reason: string) {
  const { error } = await supabase.rpc("referral_void_payout", {
    p_referral_id: id, p_reason: reason,
  });
  if (error) throw error;
}
